import React from 'react';
import {
  Body,
  Button,
  Colors,
  Dropdown,
  Flex,
  Heading,
  Menu,
} from '@cognite/cogs.js';
import styled, { css } from 'styled-components/macro';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { getValidityColor } from 'utils/runStatistics';
import { ConsistencyCollectionView } from '../types';
import { useCollectionCard } from './useCollectionCard';

type CollectionCardProps = {
  collection: ConsistencyCollectionView;
  isActive: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
};

export const CollectionCard = ({
  collection,
  isActive,
  onSelect,
  onEdit,
  onDelete,
}: CollectionCardProps) => {
  const {
    confirmingDelete,
    setConfirmingDelete,
    validPercentage,
    validityColor,
  } = useCollectionCard(collection);

  return (
    <>
      <StyledCard
        isActive={isActive}
        onClick={() => onSelect(collection.externalId)}
      >
        <StyledCardContent>
          <StyledCardHeader>
            <StyledCardHeading level={5}>{collection.name}</StyledCardHeading>
            <MenuWrapper onClick={(e) => e.stopPropagation()}>
              <Dropdown
                content={
                  <Menu>
                    <Menu.Item
                      onClick={() => onEdit(collection.externalId)}
                      aria-label="Edit collection"
                      icon="Edit"
                      iconPlacement="left"
                    >
                      <span>Edit collection</span>
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => setConfirmingDelete(true)}
                      aria-label="Delete collection"
                      icon="Delete"
                      iconPlacement="left"
                      destructive
                    >
                      <span>Delete collection</span>
                    </Menu.Item>
                  </Menu>
                }
              >
                <Button
                  icon="EllipsisVertical"
                  type="ghost"
                  size="small"
                  aria-label="Collection options"
                />
              </Dropdown>
            </MenuWrapper>
          </StyledCardHeader>
          <StyledCardMeta size="medium">
            {collection.schemaFiltersCount} schema filters •{' '}
            {collection.rulesCount} rules
          </StyledCardMeta>
          <StyledCardStatus size="medium" statusColor={validityColor}>
            {collection.lastRun ? `${validPercentage}% valid` : 'Not run'}
          </StyledCardStatus>
        </StyledCardContent>
        {collection.lastRun && (
          <StyledCardProgressBar
            progressWidth={validPercentage}
            progressColor={getValidityColor(validPercentage)}
          />
        )}
      </StyledCard>

      {confirmingDelete && (
        <ConfirmationDialog
          title="Delete collection"
          message={`Are you sure you want to delete "${collection.name}"?`}
          textOk="Delete"
          buttonTypeOk="destructive"
          onOk={() => {
            onDelete(collection.externalId);
            setConfirmingDelete(false);
          }}
          onClose={() => setConfirmingDelete(false)}
        />
      )}
    </>
  );
};

const StyledCard = styled.div<{ isActive?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 180px;
  max-width: 200px;
  padding: 1rem;
  background: ${Colors['decorative--grayscale--white']};
  border: 2px solid
    ${({ isActive }) =>
      isActive ? 'var(--cogs-primary)' : 'var(--cogs-bg-control--disabled)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ isActive }) =>
      isActive ? 'var(--cogs-primary)' : 'var(--cogs-greyscale-grey5)'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  ${({ isActive }) =>
    isActive &&
    css`
      background: var(--cogs-primary--light);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    `}
`;

const StyledCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const StyledCardHeader = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.25rem;
`;

const MenuWrapper = styled(Flex)`
  flex-shrink: 0;
  margin-top: -4px;
  margin-right: -8px;
`;

const StyledCardHeading = styled(Heading)`
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  min-height: 2.6em;
  flex: 1;
`;

const StyledCardMeta = styled(Body)`
  color: var(--cogs-greyscale-grey7);
  font-size: 11px;
  margin: 0;
`;

type StatusBodyProps = React.ComponentProps<typeof Body> & {
  statusColor: string;
};
const StatusBody = ({ statusColor: _color, ...rest }: StatusBodyProps) => (
  <Body {...rest} />
);
const StyledCardStatus = styled(StatusBody)<{ statusColor: string }>`
  color: ${({ statusColor }) => statusColor};
  font-weight: 600;
  font-size: 11px;
  margin: 0.25rem 0 0 0;
`;

const StyledCardProgressBar = styled.div<{
  progressWidth: number;
  progressColor: string;
}>`
  height: 4px;
  width: 100%;
  background: var(--cogs-greyscale-grey3);
  border-radius: 2px;
  margin-top: 0.75rem;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ progressWidth }) => progressWidth}%;
    background: ${({ progressColor }) => progressColor};
    transition: width 0.3s ease;
  }
`;
